import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import { useAuthStore } from './auth'
import { useGroupsStore } from './groups'
import { useAssignmentsStore } from './assignments'
import websocketService from '@/services/websocket.service'
import { RT_LOCAL } from '@/services/realtime-events'

interface RealTimeSubscription {
  channel: string
  filters?: Record<string, any>
  active: boolean
  subscribed_at: Date
}

interface ConnectionStatus {
  connected: boolean
  connecting: boolean
  disconnected: boolean
  reconnecting: boolean
  error: string | null
  last_connected: Date | null
  connection_attempts: number
}

// Tabla de handlers locales por evento (bus ligero)
const localHandlers = new Map<string, Set<(data:any)=>void>>()

function registerLocalHandler(event: string, handler: (data:any)=>void) {
  if (!localHandlers.has(event)) localHandlers.set(event, new Set())
  localHandlers.get(event)!.add(handler)
}

function unregisterLocalHandler(event: string, handler: (data:any)=>void) {
  if (!localHandlers.has(event)) return
  localHandlers.get(event)!.delete(handler)
}

function emitLocal(event: string, data: any) {
  if (!localHandlers.has(event)) return
  for (const h of localHandlers.get(event)!) {
    try { h(data) } catch {}
  }
}

export const useRealtimeStore = defineStore('realtime', () => {
  // 🔄 Estado Reactivo
  const connectionStatus = ref<ConnectionStatus>({
    connected: false,
    connecting: false,
    disconnected: true,
  reconnecting: false,
    error: null,
    last_connected: null,
    connection_attempts: 0
  })
  const subscriptions = ref<RealTimeSubscription[]>([])
  const lastMessage = ref<any>(null)
  const messageHistory = ref<any[]>([])
  const isInitialized = ref(false)
  /** P13-V5A-R5: the connection bridge is installed at most once per store instance. */
  let serviceBridgeInstalled = false

  // 📊 Computed Properties
  const isConnected = computed(() => connectionStatus.value.connected)
  const isConnecting = computed(() => connectionStatus.value.connecting)
  const activeSubscriptions = computed(() => 
    subscriptions.value.filter(s => s.active)
  )
  const subscriptionCount = computed(() => activeSubscriptions.value.length)
  const connectionError = computed(() => connectionStatus.value.error)

  /**
   * ── P13-V5A-R5 · DIRECTOR FINAL REPAIR · `connect()` NOW INSTALLS THE BRIDGE IT RELIES ON ──
   *
   * The comment below used to read "El estado se actualiza vía setupServiceBridge()" — and that
   * bridge was installed ONLY by `initialize()`, which nothing in the release path calls. Its one
   * caller, `useRealtimeSync`, is reachable only from `WebSocketStatus` → `RealtimeDebugPanel` →
   * `TestingView`, and `TestingView` has no route record. The `realtime` plugin (main.ts:195)
   * merely stores a reference; it never initialises.
   *
   * So `connectionStatus.connected` was NEVER written in the shipped app, and `isConnected` was
   * permanently `false`. That silently disabled every consumer of it — including, and this is the
   * reason it is repaired here, the R5 realtime truth work: `RealtimeReports` watches
   * `isConnected` to move a card to `stale` when telemetry is lost, and a watcher on a value that
   * never changes never fires. The card would have kept publishing its last figures under the
   * title "KPIs en Tiempo Real" after the producer disappeared, and the connection chip read
   * "Desconectado" permanently even while telemetry was flowing.
   *
   * The bridge is now installed by `connect()` itself, idempotently, so any consumer that connects
   * gets truthful connection state. This adds no authority and no new state: it makes an existing
   * mechanism actually run.
   */
  const connect = async (): Promise<void> => {
    setupServiceBridge()
    connectionStatus.value.connecting = true
    connectionStatus.value.error = null
    connectionStatus.value.connection_attempts++

    try {
      await websocketService.connect()
      // El estado se actualiza vía setupServiceBridge(), instalado justo arriba.
    } catch (error: any) {
      connectionStatus.value.error = `Connection failed: ${error.message}`
      connectionStatus.value.connecting = false
      console.error('Socket connection error:', error)
    }
  }

  const disconnect = (): void => {
    websocketService.disconnect()
    resetConnectionStatus()
  }

  const reconnect = async (): Promise<void> => {
    disconnect()
    await new Promise(resolve => setTimeout(resolve, 1000))
    await connect()
  }

  // PR-18: setupSocketListeners eliminado — toda la escucha de eventos
  // se hace vía websocketService (canónico) en setupServiceBridge y setupStoresIntegration

  // 📡 Channel Subscription Management — PR-18: delegado a websocketService
  const subscribe = (channel: string, filters?: Record<string, any>): void => {
    const existingSubscription = subscriptions.value.find(s => s.channel === channel)
    if (existingSubscription && existingSubscription.active) {
      console.log(`Already subscribed to channel: ${channel}`)
      return
    }

    const subscription: RealTimeSubscription = {
      channel,
      filters,
      active: true,
      subscribed_at: new Date()
    }

    if (existingSubscription) {
      const index = subscriptions.value.findIndex(s => s.channel === channel)
      subscriptions.value[index] = subscription
    } else {
      subscriptions.value.push(subscription)
    }

    console.log(`Subscribed to channel: ${channel}`)
  }

  const unsubscribe = (channel: string): void => {
    const subscription = subscriptions.value.find(s => s.channel === channel)
    if (subscription) {
      subscription.active = false
    }

    console.log(`Unsubscribed from channel: ${channel}`)
  }

  const publish = (channel: string, data: any): void => {
  }

  // 🤖 P13-K4A: auto-subscription retired.
  //
  // This built channel names out of session fields (`user:`, `role:`, `department:`, `group:`,
  // plus role dashboards) and recorded them in local `subscriptions` bookkeeping. It never reached
  // the socket — `publish` is empty and `subscribe` only pushes to an array — so it granted nothing
  // even before K4A. It is removed because the frozen contract is that the client constructs NO room
  // name at all: the backend resolves recipients from current database state on every delivery, and
  // leaving a plausible-looking room builder here is exactly how a snapshot authority gets rewired.
  const autoSubscribeUserChannels = (): void => {}

  // 📨 Real-time Update Handlers
  const handleDashboardUpdate = (data: any): void => {
    console.log('Dashboard update received:', data)
    // Forward to appropriate stores based on data type
    // This will be handled by individual stores
  }

  // P13-V1C: `handleIncidenceUpdate` retirado — la materialización de payloads del store de
  // incidencias se retiró con su leg de asignación (el evento local `incidence:update` no tiene
  // productor y el contrato congelado es señal + refetch server-scoped).

  const handleAssignmentUpdate = (data: any): void => {
    const assignmentsStore = useAssignmentsStore()
    assignmentsStore.handleRealtimeUpdate(data)
  }

  const handleGroupUpdate = (data: any): void => {
    // TODO: Integrar con groups store cuando exista handler específico
    console.debug('Group update received (no-op handler):', data)
  }

  const handleNotification = (data: any): void => {
    console.log('Real-time notification:', data)
    // Handle notifications (toast, sound, etc.)
    // This could be forwarded to a notifications store
  }

  // 🔧 Utility Functions
  const resetConnectionStatus = (): void => {
    connectionStatus.value = {
      connected: false,
      connecting: false,
      disconnected: true,
  reconnecting: false,
      error: null,
      last_connected: null,
      connection_attempts: 0
    }
  }

  const clearSubscriptions = (): void => {
    subscriptions.value.forEach(subscription => {
      if (subscription.active) {
        unsubscribe(subscription.channel)
      }
    })
    subscriptions.value = []
  }

  const clearMessageHistory = (): void => {
    messageHistory.value = []
    lastMessage.value = null
  }

  const getSubscription = (channel: string): RealTimeSubscription | undefined => {
    return subscriptions.value.find(s => s.channel === channel)
  }

  const isSubscribed = (channel: string): boolean => {
    const subscription = getSubscription(channel)
    return subscription?.active || false
  }

  // 🚀 Initialization
  const initialize = async (): Promise<void> => {
    if (isInitialized.value) {
      console.log('Realtime store already initialized')
      return
    }

    try {
  // Preparar puente de eventos ANTES de conectar
  setupServiceBridge()
      // Conectar WebSocket
      await websocketService.connect()
      
      // Setup event handlers para integrar con otros stores
      setupStoresIntegration()
      
      isInitialized.value = true
      console.log('✅ Realtime store initialized with stores integration')
    } catch (error) {
      console.error('❌ Failed to initialize realtime store:', error)
      throw error
    }
  }

  // 🔗 Integración con otros stores
  const setupStoresIntegration = (): void => {
  const assignmentsStore = useAssignmentsStore()

    // Groups updates
    // Por ahora, solo loguear hasta que groups store exponga handler
    websocketService.on(RT_LOCAL.GROUP_UPDATE, (data) => {
      console.debug('group:update (bridge no-op):', data)
    })

    // P13-V1C: el puente de incidencias (`incidence:update` → materialización del store) se retira
    // con la materialización misma — el evento no tiene productor y los consumidores canónicos de
    // incidencias son señal + refetch server-scoped.

    // Assignments updates
    websocketService.on(RT_LOCAL.ASSIGNMENT_UPDATE, (data) => {
      assignmentsStore.handleRealtimeUpdate(data)
    })

    // Dashboard updates generales
    websocketService.on(RT_LOCAL.DASHBOARD_UPDATE, (data) => {
      handleDashboardUpdate(data)
    })

    // Notificaciones
    websocketService.on(RT_LOCAL.NOTIFICATION_RECEIVED, (data) => {
      handleNotification(data)
    })

    // P13-V2B (PD-V2-04): el forward de los 7 eventos de dominio de inventario fue PURGADO con
    // sus nombres de catalogo -- cero productores desde K4A; el aviso de inventario viaja como
    // notificacion DURABLE K4B por NOTIFICATION_RECEIVED (arriba) y la reaccion canonica es el
    // refetch, no la materializacion de payloads.

    // Auto-subscribe user channels on connect
    websocketService.on(RT_LOCAL.SOCKET_CONNECT, () => {
      autoSubscribeUserChannels()
    })

    console.log('🔗 Stores integration setup complete')
  }

  /**
   * 🔌 Puente de eventos desde websocketService a nuestro estado local.
   *
   * P13-V5A-R5: ahora lo instalan TANTO `initialize()` como `connect()`, así que tiene que ser
   * idempotente — sin este guard, dos llamadas registrarían handlers duplicados sobre el mismo
   * bus local y cada evento de transporte se procesaría dos veces.
   */
  const setupServiceBridge = (): void => {
    if (serviceBridgeInstalled) return
    serviceBridgeInstalled = true

    websocketService.on(RT_LOCAL.SOCKET_CONNECT, () => {
      connectionStatus.value.connected = true
      connectionStatus.value.connecting = false
      connectionStatus.value.disconnected = false
      connectionStatus.value.reconnecting = false
      connectionStatus.value.last_connected = new Date()
      connectionStatus.value.error = null
    })
    websocketService.on(RT_LOCAL.SOCKET_DISCONNECT, () => {
      connectionStatus.value.connected = false
      connectionStatus.value.disconnected = true
      connectionStatus.value.reconnecting = true
    })
    websocketService.on(RT_LOCAL.SOCKET_RECONNECT_ERROR, () => {
      connectionStatus.value.reconnecting = true
    })
    websocketService.on(RT_LOCAL.SOCKET_RECONNECT, () => {
      connectionStatus.value.reconnecting = false
      connectionStatus.value.connected = true
      connectionStatus.value.disconnected = false
      connectionStatus.value.last_connected = new Date()
    })
  }

  const destroy = (): void => {
    clearSubscriptions()
    disconnect()
    clearMessageHistory()
    isInitialized.value = false
  }

  // API pública para registrar listeners locales
  const onEvent = (event: string, handler: (data:any)=>void) => registerLocalHandler(event, handler)
  const offEvent = (event: string, handler: (data:any)=>void) => unregisterLocalHandler(event, handler)

  return {
    // State
    connectionStatus: readonly(connectionStatus),
    subscriptions: readonly(subscriptions),
    lastMessage: readonly(lastMessage),
    messageHistory: readonly(messageHistory),
    isInitialized: readonly(isInitialized),
    
    // Computed
    isConnected,
    isConnecting,
    activeSubscriptions,
    subscriptionCount,
    connectionError,
    
    // Actions
    initialize,
    connect,
    disconnect,
    reconnect,
    subscribe,
    unsubscribe,
    publish,
    getSubscription,
    isSubscribed,
    clearSubscriptions,
    clearMessageHistory,
    destroy,
    // API pública para handlers locales
    onEvent,
    offEvent
  }
})
